import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from '@/auth/AuthProvider';
import { ToastProvider } from '@/components/ui/toast';
import { ConfirmProvider } from '@/components/ui/confirm';
import { AppShell } from '@/components/AppShell';
import { LoginPage } from '@/pages/Login';
import { SignupPage } from '@/pages/Signup';

// Code-split every page route. Initial bundle now ships only the shell,
// auth, query client, and login page; everything else loads on demand
// when the user navigates to it. Worker hashes also let the browser
// cache each route chunk independently.
const DashboardPage = lazy(() =>
  import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })),
);
const WhatsAppConnectPage = lazy(() =>
  import('@/pages/WhatsAppConnect').then((m) => ({ default: m.WhatsAppConnectPage })),
);
const UsersPage = lazy(() => import('@/pages/Users').then((m) => ({ default: m.UsersPage })));
const SettingsPage = lazy(() =>
  import('@/pages/Settings').then((m) => ({ default: m.SettingsPage })),
);
const MediaPage = lazy(() => import('@/pages/Media').then((m) => ({ default: m.MediaPage })));
const ListsPage = lazy(() => import('@/pages/Lists').then((m) => ({ default: m.ListsPage })));
const ListDetailPage = lazy(() =>
  import('@/pages/ListDetail').then((m) => ({ default: m.ListDetailPage })),
);
const CampaignsPage = lazy(() =>
  import('@/pages/Campaigns').then((m) => ({ default: m.CampaignsPage })),
);
const CampaignDetailPage = lazy(() =>
  import('@/pages/CampaignDetail').then((m) => ({ default: m.CampaignDetailPage })),
);
const InboxPage = lazy(() => import('@/pages/Inbox').then((m) => ({ default: m.InboxPage })));
const StoragePage = lazy(() => import('@/pages/Storage').then((m) => ({ default: m.StoragePage })));
const TemplatesPage = lazy(() => import('@/pages/Templates').then((m) => ({ default: m.TemplatesPage })));
const VariablesPage = lazy(() => import('@/pages/Variables').then((m) => ({ default: m.VariablesPage })));
const ShortLinksPage = lazy(() => import('@/pages/ShortLinks').then((m) => ({ default: m.ShortLinksPage })));
const OrganizationsPage = lazy(() => import('@/pages/Organizations').then((m) => ({ default: m.OrganizationsPage })));
const LandingPage = lazy(() => import('@/pages/Landing').then((m) => ({ default: m.LandingPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  if (state.status === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (state.status !== 'authenticated') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  if (state.status === 'loading') return null;
  if (
    state.status !== 'authenticated' ||
    (state.user.role !== 'admin' && !state.user.is_super_admin)
  ) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const { state } = useAuth();
  if (state.status === 'loading') return null;
  if (state.status !== 'authenticated' || !state.user.is_super_admin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function RouteFallback() {
  return (
    <div className="grid place-items-center py-24 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}

// Smart router for "/". Logged-out visitors see the public marketing
// landing; logged-in users get bounced straight to their dashboard so
// they never see the homepage twice. Mirrors Notion / Linear / Slack.
function HomeRouter() {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (state.status === 'authenticated') {
    // Preserve `?organization_id=...` (and any other query) on the
    // redirect so super_admin's "View as org" link from the
    // Organizations page actually scopes the destination. Stripping
    // the query here was an org-isolation leak: the AppShell never
    // saw the scope param and every query defaulted to the user's
    // home org regardless of which org they clicked "View" on.
    return <Navigate to={`/dashboard${location.search}`} replace />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <LandingPage />
    </Suspense>
  );
}

function Routed() {
  return (
    <Routes>
      <Route path="/" element={<HomeRouter />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route
          path="dashboard"
          element={
            <Suspense fallback={<RouteFallback />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route
          path="whatsapp"
          element={
            <Suspense fallback={<RouteFallback />}>
              <WhatsAppConnectPage />
            </Suspense>
          }
        />
        <Route
          path="campaigns"
          element={
            <Suspense fallback={<RouteFallback />}>
              <CampaignsPage />
            </Suspense>
          }
        />
        <Route
          path="campaigns/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <CampaignDetailPage />
            </Suspense>
          }
        />
        <Route
          path="inbox"
          element={
            <Suspense fallback={<RouteFallback />}>
              <InboxPage />
            </Suspense>
          }
        />
        <Route
          path="lists"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ListsPage />
            </Suspense>
          }
        />
        <Route
          path="lists/:id"
          element={
            <Suspense fallback={<RouteFallback />}>
              <ListDetailPage />
            </Suspense>
          }
        />
        <Route
          path="media"
          element={
            <Suspense fallback={<RouteFallback />}>
              <MediaPage />
            </Suspense>
          }
        />
        <Route
          path="users"
          element={
            <RequireAdmin>
              <Suspense fallback={<RouteFallback />}>
                <UsersPage />
              </Suspense>
            </RequireAdmin>
          }
        />
        <Route
          path="settings"
          element={
            <RequireAdmin>
              <Suspense fallback={<RouteFallback />}>
                <SettingsPage />
              </Suspense>
            </RequireAdmin>
          }
        />
        <Route
          path="storage"
          element={
            <RequireAdmin>
              <Suspense fallback={<RouteFallback />}>
                <StoragePage />
              </Suspense>
            </RequireAdmin>
          }
        />
        <Route
          path="templates"
          element={
            <RequireAuth>
              <Suspense fallback={<RouteFallback />}>
                <TemplatesPage />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="variables"
          element={
            <RequireAdmin>
              <Suspense fallback={<RouteFallback />}>
                <VariablesPage />
              </Suspense>
            </RequireAdmin>
          }
        />
        <Route
          path="short-links"
          element={
            <RequireAuth>
              <Suspense fallback={<RouteFallback />}>
                <ShortLinksPage />
              </Suspense>
            </RequireAuth>
          }
        />
        <Route
          path="organizations"
          element={
            <RequireSuperAdmin>
              <Suspense fallback={<RouteFallback />}>
                <OrganizationsPage />
              </Suspense>
            </RequireSuperAdmin>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <AuthProvider>
            <BrowserRouter>
              <Routed />
            </BrowserRouter>
          </AuthProvider>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
